import React, {useContext, useEffect, useState} from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {Col, Row, Spinner, Table} from "react-bootstrap";
import AppContext from "../contexts/app-context";
import * as backendUtils from "../helper/BackendUtils";
import './Stats.css';

// ── Constants ────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const heatColor = (n) => {
    if (!n || n === 0) return '#ebedf0';
    if (n <= 2) return '#ffd8c2';
    if (n <= 5) return '#ff9a6c';
    if (n <= 10) return '#ff5800';
    return '#c43200';
};

const toDateStr = (d) => {
    return d.toISOString().split('T')[0];
};

// ── Helpers ──────────────────────────────────────────────────
function buildMonthCalendars(data) {
    const map = {};
    data.forEach(d => {
        map[d.date] = Number(d.count);
    });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 364);

    // Collect unique year-month pairs in range
    const seen = new Set();
    const monthsList = [];
    const cur = new Date(startDate);
    while (cur <= today) {
        const key = `${cur.getFullYear()}-${cur.getMonth()}`;
        if (!seen.has(key)) {
            seen.add(key);
            monthsList.push({year: cur.getFullYear(), month: cur.getMonth()});
        }
        cur.setDate(cur.getDate() + 1);
    }

    return monthsList.map(({year, month}) => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const firstDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // 0=Mon

        // Flat cell array: null = padding, count=-1 = out-of-range
        const cells = Array(firstDow).fill(null);
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateObj = new Date(year, month, d);
            if (dateObj > today || dateObj < startDate) {
                cells.push({dateStr: toDateStr(dateObj), count: -1});
            } else {
                const s = toDateStr(dateObj);
                cells.push({dateStr: s, count: map[s] || 0});
            }
        }
        // Chunk into weeks of 7
        const weeks = [];
        for (let i = 0; i < cells.length; i += 7) {
            const w = cells.slice(i, i + 7);
            while (w.length < 7) w.push(null);
            weeks.push(w);
        }
        return {name: MONTHS[month], year, weeks};
    });
}

function isoWeekKey(dateStr) {
    const parts = dateStr.split('-').map(Number);
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    const day = d.getUTCDay() || 7; // 1=Mon … 7=Sun
    // Rewind to Monday of this week
    const mon = new Date(d);
    mon.setUTCDate(d.getUTCDate() - (day - 1));
    const y = mon.getUTCFullYear();
    const m = String(mon.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(mon.getUTCDate()).padStart(2, '0');
    return {
        sortKey: `${y}-${m}-${dd}`,
        label: `${mon.getUTCDate()} ${MONTHS[mon.getUTCMonth()]}`
    };
}

function buildWeeklyStats(allRides) {
    const map = {};
    allRides.filter(r => r.direction === 1 || r.direction === 2).forEach(r => {
        const {sortKey, label} = isoWeekKey(r.date);
        if (!map[sortKey]) map[sortKey] = {week: label, km: 0, rides: 0};
        map[sortKey].km += r.distance > 0 ? r.distance / 1000 : 0;
        map[sortKey].rides++;
    });

    const sortedKeys = Object.keys(map).sort();
    if (sortedKeys.length === 0) return [];

    // Monday of the first-ever ride week
    const [fy, fm, fd] = sortedKeys[0].split('-').map(Number);
    const cur = new Date(Date.UTC(fy, fm - 1, fd));

    // Monday of this week (UTC)
    const now = new Date();
    const nowDow = now.getUTCDay() || 7;
    const thisMonday = new Date(Date.UTC(
        now.getUTCFullYear(), now.getUTCMonth(),
        now.getUTCDate() - (nowDow - 1)
    ));

    const result = [];
    while (cur <= thisMonday) {
        const y = cur.getUTCFullYear();
        const mo = String(cur.getUTCMonth() + 1).padStart(2, '0');
        const d = String(cur.getUTCDate()).padStart(2, '0');
        const key = `${y}-${mo}-${d}`;
        const label = `${cur.getUTCDate()} ${MONTHS[cur.getUTCMonth()]}`;
        const entry = map[key];
        result.push(entry
            ? {week: entry.week, km: parseFloat(entry.km.toFixed(1)), rides: entry.rides}
            : {week: label, km: 0, rides: 0}
        );
        cur.setUTCDate(cur.getUTCDate() + 7);
    }
    return result;
}

// ── Sub-components ───────────────────────────────────────────
const Card = ({title, children, style}) => (
    <div className="stats-card" style={style}>
        {title && <div className="stats-card-title">{title}</div>}
        {children}
    </div>
);

const SectionHeader = ({children}) => <div className="stats-section-header">{children}</div>;

// ── Main Component ───────────────────────────────────────────
export default function Stats() {
    const context = useContext(AppContext);
    const email = context.userId;
    const uri = context.backendUri;
    const shortEmail = email ? email.slice(0, -22) : '';

    const [monthCalendars, setMonthCalendars] = useState([]);
    const [weekdayData, setWeekdayData] = useState([]);
    const [yearlyPart, setYearlyPart] = useState([]);
    const [yearlyKm, setYearlyKm] = useState([]);
    const [yearlyRides, setYearlyRides] = useState([]);
    const [groupStrava, setGroupStrava] = useState(null);
    const [eventHistory, setEventHistory] = useState([]);
    const [weeklyStats, setWeeklyStats] = useState([]);
    const [heatLoading, setHeatLoading] = useState(true);

    // 1 · Heatmap + weekly rhythm
    useEffect(() => {
        if (!uri) return;
        backendUtils.getRequest(`${uri}/stats/heatmap`, {}, resp => {
            const data = resp.data;
            setMonthCalendars(buildMonthCalendars(data));
            const totals = [0, 0, 0, 0, 0, 0, 0];
            data.forEach(d => {
                const dow = new Date(d.date + 'T12:00:00').getDay();
                totals[dow === 0 ? 6 : dow - 1] += Number(d.count);
            });
            setWeekdayData(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                .map((name, i) => ({name, rides: totals[i]})));
            setHeatLoading(false);
        }, err => {
            console.error(err);
            setHeatLoading(false);
        }).catch(console.error);
    }, [uri]);

    // 2 · Year-to-Year participants
    useEffect(() => {
        if (!uri) return;
        backendUtils.getRequest(`${uri}/stats/yearly/participants`, {}, resp => {
            setYearlyPart(resp.data.map(d => ({year: String(d.year), count: Number(d.count)})));
        }, console.error).catch(console.error);
    }, [uri]);

    // 3 · Year-to-Year km
    useEffect(() => {
        if (!uri) return;
        backendUtils.getRequest(`${uri}/stats/yearly/distance`, {}, resp => {
            setYearlyKm(resp.data.map(d => ({
                year: String(d.year),
                km: parseFloat((d.totalDistance / 1000).toFixed(1))
            })));
        }, console.error).catch(console.error);
    }, [uri]);

    // 4 · Year-to-Year rides HM / MH (summed)
    useEffect(() => {
        if (!uri) return;
        backendUtils.getRequest(`${uri}/stats/yearly/rides`, {}, resp => {
            setYearlyRides(resp.data.map(d => ({
                year: String(d.year),
                rides: Number(d.hmCount) + Number(d.mhCount)
            })));
        }, console.error).catch(console.error);
    }, [uri]);

    // 5 · Group strava vs manual (current year)
    useEffect(() => {
        if (!uri) return;
        backendUtils.getRequest(`${uri}/stats/group/strava`, {}, resp => {
            setGroupStrava([
                {name: 'Strava', value: Number(resp.data.strava), color: '#fc4c02'},
                {name: 'Manual', value: Number(resp.data.manual), color: '#607d8b'},
            ]);
        }, console.error).catch(console.error);
    }, [uri]);

    // 6 · Personal event history
    useEffect(() => {
        if (!uri || !email) return;
        backendUtils.getRequest(`${uri}/stats/participant/${email}/eventCommutes`, {}, resp => {
            setEventHistory(
                resp.data.map(e => ({...e, distance: parseFloat(e.distance / 1000).toFixed(1) / 1.0}))
                    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
            );
        }, console.error).catch(console.error);
    }, [uri, email]);

    // 7 · Personal week-to-week (derived from all-rides)
    useEffect(() => {
        if (!uri || !email) return;
        backendUtils.getRequest(`${uri}/${email}`, {}, resp => {
            setWeeklyStats(buildWeeklyStats(resp.data));
        }, console.error).catch(console.error);
    }, [uri, email]);

    // Merged dataset: km (bars) + total rides (line) by year
    const yearlyKmRides = React.useMemo(() => {
        const map = {};
        yearlyKm.forEach(d => {
            map[d.year] = {...(map[d.year] || {}), year: d.year, km: d.km};
        });
        yearlyRides.forEach(d => {
            map[d.year] = {...(map[d.year] || {}), year: d.year, rides: d.rides};
        });
        return Object.values(map).sort((a, b) => a.year.localeCompare(b.year));
    }, [yearlyKm, yearlyRides]);

    const CHART_H = 170;

    return (
        <div className="stats-page">
            <h5 className="text-center mb-2">📊 Statistics</h5>

            {/* ════════════════ GENERAL STATISTICS ════════════════ */}
            <SectionHeader>General Statistics</SectionHeader>

            {/* Row 1: two YtY charts */}
            <Row className="g-3">
                <Col xs={12} md={4}>
                    <Card title="Year-to-Year Participants">
                        <ResponsiveContainer width="100%" height={CHART_H}>
                            <AreaChart data={yearlyPart} margin={{top: 4, right: 4, left: -20, bottom: 0}}>
                                <defs>
                                    <linearGradient id="gradPart" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ff5800" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#ff5800" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                <XAxis dataKey="year" tick={{fontSize: 9}} tickLine={false}/>
                                <YAxis tick={{fontSize: 9}} tickLine={false} axisLine={false} allowDecimals={false}/>
                                <Tooltip/>
                                <Area type="monotone" dataKey="count" name="Participants"
                                      stroke="#ff5800" fill="url(#gradPart)" strokeWidth={2} dot={{r: 3}}/>
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col xs={12} md={8}>
                    <Card title="Year-to-Year KM & Rides">
                        <ResponsiveContainer width="100%" height={CHART_H}>
                            <ComposedChart data={yearlyKmRides} margin={{top: 4, right: 8, left: -20, bottom: 0}}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                <XAxis dataKey="year" tick={{fontSize: 9}} tickLine={false}/>
                                <YAxis yAxisId="left" tick={{fontSize: 9}} tickLine={false} axisLine={false}/>
                                <YAxis yAxisId="right" orientation="right" tick={{fontSize: 9}} tickLine={false}
                                       axisLine={false} allowDecimals={false}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{fontSize: 9}}/>
                                <Bar yAxisId="left" dataKey="km" name="Total km" fill="#ff5800" radius={[3, 3, 0, 0]}
                                     barSize={28} opacity={0.85}/>
                                <Line yAxisId="right" type="monotone" dataKey="rides" name="Rides" stroke="#2196f3"
                                      strokeWidth={2} dot={{r: 3}}/>
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            {/* Row 2: heatmap full width */}
            <Row className="g-3">
                <Col xs={12}>
                    <Card title="Group Activity Heatmap (last 52 weeks)">
                        {heatLoading
                            ?
                            <div className="text-center py-2"><Spinner animation="border" size="sm" variant="warning"/>
                            </div>
                            : <>
                                <div className="heatmap-months-row">
                                    {monthCalendars.map((mon, mi) => (
                                        <div key={mi} className="heatmap-month-block">
                                            <div className="heatmap-month-name">{mon.name}</div>
                                            <div className="heatmap-month-grid">
                                                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                                                    <div key={i} className="heatmap-day-header">{d}</div>
                                                ))}
                                                {mon.weeks.map((week, wi) =>
                                                    week.map((cell, di) => (
                                                        cell === null || cell.count < 0
                                                            ? <div key={`${wi}-${di}`} className="heatmap-cell"
                                                                   style={{background: 'transparent'}}/>
                                                            : <div key={`${wi}-${di}`} className="heatmap-cell"
                                                                   style={{background: heatColor(cell.count)}}
                                                                   title={`${cell.dateStr}: ${cell.count} ride${cell.count !== 1 ? 's' : ''}`}/>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="heatmap-legend">
                                    <span className="text-muted" style={{fontSize: 9}}>Less</span>
                                    {[0, 1, 3, 6, 11].map(v => (
                                        <div key={v} className="heatmap-cell"
                                             style={{background: heatColor(v), display: 'inline-block'}}/>
                                    ))}
                                    <span className="text-muted" style={{fontSize: 9}}>More</span>
                                </div>
                            </>
                        }
                    </Card>
                </Col>
            </Row>

            {/* Row 3: weekly rhythm + group strava */}
            <Row className="g-3">
                <Col xs={12} md={8}>
                    <Card title="Weekly Riding Rhythm (all time)">
                        <ResponsiveContainer width="100%" height={CHART_H}>
                            <BarChart data={weekdayData} barCategoryGap="30%"
                                      margin={{top: 4, right: 4, left: -20, bottom: 0}}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                <XAxis dataKey="name" tick={{fontSize: 9}} tickLine={false}/>
                                <YAxis tick={{fontSize: 9}} tickLine={false} axisLine={false} allowDecimals={false}/>
                                <Tooltip cursor={{fill: 'rgba(255,88,0,0.08)'}}/>
                                <Bar dataKey="rides" radius={[3, 3, 0, 0]}>
                                    {weekdayData.map((_, i) => (
                                        <Cell key={i} fill={i < 5 ? '#ff5800' : '#ccc'}/>
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col xs={12} md={4}>
                    <Card title={`Strava vs Manual – ${new Date().getFullYear()}`}>
                        {groupStrava && (
                            <>
                                <ResponsiveContainer width="100%" height={130}>
                                    <PieChart>
                                        <Pie data={groupStrava} dataKey="value" nameKey="name"
                                             cx="50%" cy="50%" outerRadius={55} innerRadius={28} paddingAngle={3}
                                             label={({
                                                         name,
                                                         percent
                                                     }) => percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}>
                                            {groupStrava.map((e, i) => <Cell key={i} fill={e.color}/>)}
                                        </Pie>
                                        <Tooltip formatter={v => [`${v} rides`]}/>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="d-flex flex-column gap-1 mt-1">
                                    {groupStrava.map((s, i) => (
                                        <div key={i} className="d-flex align-items-center">
                                            <div className="stats-dot" style={{background: s.color}}/>
                                            <span style={{fontSize: '0.72rem'}}><b>{s.value}</b> {s.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* ════════════════ PERSONAL STATISTICS ═══════════════ */}
            <SectionHeader>Personal Statistics</SectionHeader>

            {/* Row 4: week-to-week */}
            <Row className="g-3">
                <Col xs={12}>
                    <Card title="Week-to-Week KM / Rides">
                        {weeklyStats.length === 0
                            ? <p className="text-muted text-center" style={{fontSize: '0.75rem'}}>No ride data yet.</p>
                            : <ResponsiveContainer width="100%" height={200}>
                                <ComposedChart data={weeklyStats} margin={{top: 4, right: 8, left: -20, bottom: 24}}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                    <XAxis dataKey="week" tick={{fontSize: 8}} tickLine={false}
                                           interval="preserveStartEnd" angle={-35} textAnchor="end"/>
                                    <YAxis yAxisId="left" tick={{fontSize: 9}} tickLine={false} axisLine={false}/>
                                    <YAxis yAxisId="right" orientation="right" tick={{fontSize: 9}} tickLine={false}
                                           axisLine={false} allowDecimals={false}/>
                                    <Tooltip/>
                                    <Legend wrapperStyle={{fontSize: 9}}/>
                                    <Bar yAxisId="left" dataKey="km" name="KM" fill="#ff5800" radius={[3, 3, 0, 0]}
                                         barSize={14} opacity={0.85}/>
                                    <Line yAxisId="right" type="monotone" dataKey="rides" name="Rides" stroke="#2196f3"
                                          strokeWidth={2} dot={{r: 2}}/>
                                </ComposedChart>
                            </ResponsiveContainer>
                        }
                    </Card>
                </Col>
            </Row>

            {/* Row 5: event history table */}
            <Row className="g-3">
                <Col xs={12}>
                    <Card title="My Event History">
                        {eventHistory.length === 0
                            ? <p className="text-muted text-center" style={{fontSize: '0.75rem'}}>No events yet.</p>
                            :
                            <Table className="stats-table" striped hover responsive size="sm" style={{marginBottom: 0}}>
                                <thead>
                                <tr>
                                    <th>Event</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th>Rides</th>
                                    <th>Dist (km)</th>
                                </tr>
                                </thead>
                                <tbody>
                                {eventHistory.map((e, i) => (
                                    <tr key={i}>
                                        <td>{e.name}</td>
                                        <td>{e.startDate}</td>
                                        <td>{e.endDate}</td>
                                        <td><strong>{e.rides}</strong></td>
                                        <td>{e.distance > 0 ? e.distance : '—'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        }
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

